import { useCallback, useState } from "react";

import { useLazyGetCompanyQuery } from "@/pages/Private/redux/companies/companies.api";

import { useLoginAs } from "./useLoginAs";

interface UseLoginAsAndNavigateReturn {
	loginAsAndNavigate: (companyId: number, listId: number) => Promise<void>;
	isLoading: boolean;
	error: string | null;
}

/**
 * The Support Dashboard's "Login as": the company's first admin, landing on the list's copywriting
 * page. The login mechanics live in `useLoginAs` (shared with the staff ticket detail, PREC-3940).
 */
export const useLoginAsAndNavigate = (): UseLoginAsAndNavigateReturn => {
	const { loginAs, isLoading: isLoggingIn } = useLoginAs();
	const [getCompany] = useLazyGetCompanyQuery();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const loginAsAndNavigate = useCallback(
		async (companyId: number, listId: number) => {
			setIsLoading(true);
			setError(null);

			try {
				const company = await getCompany(companyId).unwrap();

				if (!company.admins || company.admins.length === 0) {
					setError("No admin users found for this company");

					return;
				}

				const ok = await loginAs(company.admins[0].id, `/app/lists/create/copywriting/${listId}`);

				if (!ok) {
					setError("Failed to login as company admin");
				}
			} catch {
				setError("Failed to login as company admin");
			} finally {
				setIsLoading(false);
			}
		},
		[getCompany, loginAs]
	);

	return { loginAsAndNavigate, isLoading: isLoading || isLoggingIn, error };
};
